import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCreateSummarizer, useGetSummarizer } from "@packages/api/madlenAPI";
import { getEnvironment } from "@packages/utils";
import { useToast } from "@packages/contexts/ToastContext";
import SummaryCard from "../../../components/summaries/SummaryCard";
import ErrorComponent from "@features/tools/components/error-component";
import { TextLinear, Button, Text } from "@core/index";
import { SimpleLoading } from "@core/loading";
import {
  SummarizerCreateRequestDtoGenerationType,
  SummarizerCreateRequestDtoContentLanguage,
  SummarizerCreateRequestDtoCefrLevel,
  TeacherDocumentResponseDto,
} from "@packages/api/madlenAPI.schemas";
import { FileUploader } from "@core/file-uploader";
import { trackEvent } from "@packages/hooks/useAnalytics";
import ModeToggle from "../../../../common/components/mode-toggle";
import { updateLastUsedTools } from "@packages/store/userSlice";
import { useAppDispatch } from "@packages/store/hooks";
import CEFRSelector from "@core/cefr-selector/CEFRSelector";
import enFlag from "@packages/assets/en.png";
import trFlag from "@packages/assets/tr.png";
import deFlag from "@packages/assets/de.png";
import frFlag from "@packages/assets/fr.png";

type GeneratorMode = "document" | "text";
const CHARACTER_LIMIT = 200000;

const CreateSummaryView = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [input, setInput] = useState({
    originalText: "",
  });
  const [generatorMode, setGeneratorMode] = useState<GeneratorMode>("text");
  const [uploadedFiles, setUploadedFiles] = useState<
    TeacherDocumentResponseDto[]
  >([]);
  const [contentLanguage, setContentLanguage] = useState<
    "en" | "tr" | "de" | "fr"
  >("en");
  const [cefrLevel, setCefrLevel] =
    useState<SummarizerCreateRequestDtoCefrLevel>(
      SummarizerCreateRequestDtoCefrLevel.A1,
    );
  const dispatch = useAppDispatch();
  const { mutateAsync: createSummarizer, data: summarizer } =
    useCreateSummarizer();
  const { addToast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const { trackEvent: track } = trackEvent();

  const acceptedFileTypes = {
    "application/pdf": [".pdf"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
      ".docx",
    ],
    "text/plain": [".txt"],
    "image/png": [".png"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/jpg": [".jpg", ".jpeg"],
  };

  const { data: summarizerData } = useGetSummarizer(summarizer?.id!, {
    query: {
      enabled: !!summarizer?.id,
      refetchInterval: 2000,
    },
  });

  useEffect(() => {
    if (summarizerData?.markdownSummary) {
      setIsLoading(false);
    }
  }, [isLoading, summarizerData]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (
      (generatorMode === "text" && input.originalText.trim() === "") ||
      (generatorMode === "document" && uploadedFiles.length === 0)
    ) {
      addToast({
        duration: 5000,
        title: t("summaries.create.error.empty"),
        variant: "error",
      });
      return;
    }
    if (
      generatorMode === "text" &&
      input.originalText.length > CHARACTER_LIMIT
    ) {
      addToast({
        duration: 5000,
        title: t("summaries.create.error.tooLong"),
        description: t("summaries.create.error.characterLimit", {
          limit: CHARACTER_LIMIT.toLocaleString(),
        }),
        variant: "error",
      });
      return;
    }

    const teacherDocumentIds = uploadedFiles
      .map((file) => file.id)
      .filter((id): id is number => id !== undefined);

    try {
      setIsLoading(true);
      setError(null);

      const result = await createSummarizer({
        data: {
          originalText: input.originalText,
          environment: getEnvironment(),
          teacherDocumentIds,
          contentLanguage:
            contentLanguage as SummarizerCreateRequestDtoContentLanguage,
          cefrLevel:
            contentLanguage === "en"
              ? cefrLevel
              : SummarizerCreateRequestDtoCefrLevel.A1,
          generationType:
            generatorMode === "document"
              ? SummarizerCreateRequestDtoGenerationType.TEACHER_DOCUMENT
              : SummarizerCreateRequestDtoGenerationType.PROMPT,
        },
      });

      if (!result?.id) {
        console.error("No summarizer ID returned");
      }
      dispatch(updateLastUsedTools("summaries"));
    } catch (error: any) {
      setIsLoading(false);
      if (
        error.response?.data.message ===
        "Content contains inappropriate language"
      ) {
        addToast({
          title: t("common.inappropriateContent.title"),
          description: t("common.inappropriateContent.description"),
          variant: "error",
          duration: 3000,
        });
      } else {
        setError(error as Error);
        console.error("Summarizer error:", error);
        addToast({
          title: t("summaries.create.error.title"),
          description: t("summaries.create.error.description"),
          variant: "error",
        });
      }
    }
  };

  const handleFileUploadError = (error: string) => {
    addToast({
      title: t("assistant.uploadError"),
      description: error,
      variant: "error",
      duration: 5000,
    });
  };

  const handleFileUploadSuccess = async (
    url: string,
    response?: TeacherDocumentResponseDto,
  ) => {
    track(
      {
        category: "ContentFileUpload",
        action: `summaries.create.file.upload.success`,
        label: `SummariesCreate file upload success`,
        nonInteraction: false,
      },
      {
        location: "/summaries-create/file-upload",
        element: "file-upload",
      },
    );

    if (!response) {
      console.error("No response received from upload");
      addToast({
        title: t("assistant.uploadError"),
        description: t("assistant.uploadErrorDescription"),
        variant: "error",
        duration: 5000,
      });
      return;
    }

    setUploadedFiles((prevFiles) => {
      const newFiles = [...prevFiles, response];
      return newFiles;
    });
  };

  if (summarizerData?.markdownSummary) {
    return (
      <div className="flex-1 p-3 sm:p-4 md:p-6 lg:p-8 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
        <div className="max-w-7xl mx-auto">
          <div className="mb-4 sm:mb-6 lg:mb-8">
            <Button
              onClick={() => navigate("/content/summaries")}
              size="md"
              rounded="full"
              bgColor="bg-white dark:bg-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700"
              textColor="text-gray-700 dark:text-gray-200"
              variant="outline"
              className="border-gray-300 dark:border-gray-600"
              leftIcon={<ArrowLeft size={20} />}
            >
              {t("common.back")}
            </Button>
          </div>

          <SummaryCard
            onCreateAgain={() => {
              handleSubmit();
            }}
            createdAt={summarizerData?.createdAt ?? ""}
            content={summarizerData?.markdownSummary}
            imageUrl={summarizerData?.imageUrl}
            videoUrl={summarizerData?.videoUrl}
            author={summarizerData?.authorName ?? ""}
          />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <SimpleLoading
        fullHeight={true}
        showProgress={true}
        duration={20}
        fastLoading={false}
      />
    );
  }

  if (error) {
    return (
      <ErrorComponent
        title={t("summaries.create.error.title")}
        description={t("summaries.create.error.description")}
      />
    );
  }

  return (
    <div className="flex-1 p-3 sm:p-4 md:p-6 lg:p-8 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        {/* Header with title and back button */}
        <div className="mb-4 sm:mb-6 lg:mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
          <div className="min-w-0 flex-1">
            <Text
              variant="h1"
              className="text-lg sm:text-xl lg:text-2xl xl:text-3xl break-words"
            >
              {t("summaries.create.title")}
            </Text>
            <Text
              variant="p"
              className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base break-words"
            >
              {t("summaries.create.description")}
            </Text>
          </div>
          <div className="flex-shrink-0">
            <Button
              onClick={() => navigate("/content/summaries")}
              size="md"
              rounded="full"
              bgColor="bg-white dark:bg-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700"
              textColor="text-gray-700 dark:text-gray-200"
              variant="outline"
              className="border-gray-300 dark:border-gray-600"
              leftIcon={<ArrowLeft size={20} />}
            >
              {t("common.back")}
            </Button>
          </div>
        </div>

        {/* Main content card */}
        <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-dark-200 dark:border-dark-400 p-3 sm:p-4 lg:p-6 mt-3 sm:mt-4 lg:mt-6">
          <Text
            variant="h2"
            className="mb-3 sm:mb-4 text-base sm:text-lg lg:text-xl xl:text-2xl break-words"
          >
            {t("summaries.create.formTitle")}
          </Text>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Input mode toggle */}
            <ModeToggle
              value={generatorMode}
              onChange={(value) => {
                setGeneratorMode(value as GeneratorMode);
                setInput({ ...input, originalText: "" });
                setUploadedFiles([]);
              }}
              title={t("summaries.create.selectInputType")}
              theme="green"
            />

            {/* Content Language and CEFR Level in grid */}
            <div className="">
              {/* Content Language Selector */}

              {/* CEFR Level Selector - Only show for English */}
              {contentLanguage === "en" && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t("summaries.cefrLevel")}
                  </label>
                  <CEFRSelector
                    selectedLevel={cefrLevel}
                    onLevelChange={setCefrLevel}
                    themeColor="green"
                    className="mt-1"
                  />
                </div>
              )}
            </div>

            {/* Input content area */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {generatorMode === "text"
                  ? t("summaries.create.originalText")
                  : t("summaries.create.uploadDocumentTitle")}
              </label>

              {generatorMode === "document" && (
                <FileUploader
                  accept={acceptedFileTypes}
                  onUploadSuccess={handleFileUploadSuccess}
                  onFileDelete={() => setUploadedFiles([])}
                  onUploadError={handleFileUploadError}
                  dragActiveText={t("summaries.create.dropDocument")}
                  dragInactiveText={t("summaries.create.uploadDocument")}
                  uploadColor="green"
                  extractColor="green"
                  successColor="green"
                  shouldExtractContent
                />
              )}

              {generatorMode === "text" && (
                <textarea
                  value={input.originalText}
                  data-testid="summary-text-input"
                  onChange={(e) =>
                    setInput({ ...input, originalText: e.target.value })
                  }
                  rows={12}
                  className="w-full rounded-xl border-2 border-gray-200 dark:border-gray-600 p-4 text-base focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-dark-500 dark:text-gray-100 transition-all duration-200 placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder={t("summaries.create.originalTextPlaceholder")}
                />
              )}
            </div>
          </form>
        </div>

        {/* Generate button */}
        <div className="mt-4 sm:mt-6 flex justify-end">
          <Button
            onClick={handleSubmit}
            size="md"
            rounded="full"
            bgColor="bg-teal-500 dark:bg-teal-500 hover:bg-teal-600 dark:hover:bg-teal-600"
            textColor="text-white"
            className={`px-6 sm:px-8 lg:px-12 ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={
              isLoading ||
              (generatorMode === "text" && !input.originalText) ||
              (generatorMode === "document" && uploadedFiles.length === 0)
            }
            data-testid="generate-summary-button"
          >
            {t("summaries.create.generateSummary")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateSummaryView;
